import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Configuration from "./pages/Configuration";
import ProductSync from "./pages/ProductSync";
import ProductSyncChecker from "./pages/ProductSyncChecker";
import InventorySync from "./pages/InventorySync";
import SupplierShipments from "./pages/SupplierShipments";
import CustomerShipments from "./pages/CustomerShipments";
import EdiShipments from "./pages/EdiShipments";
import WholesaleRouting from "./pages/WholesaleRouting";
import Layout from "./components/Layout";
import Profile from "./pages/Profile";
import UserManagement from "./pages/UserManagement";

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return null;
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();
  if (isLoading) return null;
  const isAdmin = user?.role === "admin" || user?.role === "superadmin" || user?.is_admin;
  return isAdmin ? <>{children}</> : <Navigate to="/" />;
};

const SuperadminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();
  if (isLoading) return null;
  const isSuper = user?.role === "superadmin";
  return isSuper ? <>{children}</> : <Navigate to="/" />;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/"
        element={
          <PrivateRoute>
            <Layout />
          </PrivateRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="profile" element={<Profile />} />
        <Route
          path="users"
          element={
            <SuperadminRoute>
              <UserManagement />
            </SuperadminRoute>
          }
        />
        <Route
          path="product-sync"
          element={
            <AdminRoute>
              <ProductSync />
            </AdminRoute>
          }
        />
        <Route
          path="product-sync/checker"
          element={
            <AdminRoute>
              <ProductSyncChecker />
            </AdminRoute>
          }
        />
        <Route
          path="inventory-sync"
          element={
            <AdminRoute>
              <InventorySync />
            </AdminRoute>
          }
        />
        <Route
          path="supplier-shipments"
          element={
            <AdminRoute>
              <SupplierShipments />
            </AdminRoute>
          }
        />
        <Route
          path="customer-shipments"
          element={
            <AdminRoute>
              <CustomerShipments />
            </AdminRoute>
          }
        />
        <Route
          path="edi-shipments"
          element={
            <AdminRoute>
              <EdiShipments />
            </AdminRoute>
          }
        />
        <Route
          path="edi-routing"
          element={<WholesaleRouting />}
        />
        <Route
          path="configuration"
          element={
            <AdminRoute>
              <Configuration />
            </AdminRoute>
          }
        />
      </Route>
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <Router>
        <AppRoutes />
        <Toaster position="top-right" />
      </Router>
    </AuthProvider>
  );
};

export default App;
